import { BookOpen, Pencil, MapPin, Mail, Phone, Building2, LucideIcon, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatMoney } from '@/utils/currency';

export interface StatConfig {
  value: number;
  label: string;
  icon: LucideIcon;
  iconColorClass: string;
  bgClass: string;
}

export interface ActionConfig {
  label: string;
  icon: LucideIcon;
  onClick: () => void;
  textClass: string;
  bgClass: string;
  hoverClass: string;
}

interface ContactCardProps {
  name: string;
  onEdit?: () => void;
  onLedger?: () => void;
  onDelete?: () => void;
  stats: StatConfig[];
  progressLabel: string;
  progressPercentage: number;
  progressSuffix?: string;

  phone?: string;
  email?: string;
  address?: string;

  actions: ActionConfig[];
  isDefault?: boolean;
  subtitle?: string;
}

export default function ContactCard({
  name,
  onEdit,
  onLedger,
  onDelete,
  stats,
  progressLabel,
  progressPercentage,
  progressSuffix = 'Paid',
  phone,
  email,
  address,
  actions,
  isDefault,
  subtitle,
}: ContactCardProps) {
  const { t } = useTranslation();
  const barWidth = Math.min(100, Math.max(0, progressPercentage));
  const statGridClass =
    stats.length >= 4
      ? 'grid grid-cols-2 lg:grid-cols-4 gap-3 mt-5'
      : 'grid grid-cols-2 lg:grid-cols-3 gap-3 mt-5';

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden hover:border-primary-200 transition-colors group cursor-pointer h-full flex flex-col">
      <div className="h-1.5 bg-blue-500 shrink-0" />

      <div className="p-4 sm:p-5 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-11 h-11 rounded-lg bg-blue-50 flex items-center justify-center overflow-hidden shrink-0">
              <Building2 className="w-6 h-6 text-blue-600" />
            </div>
            <div className="min-w-0">
              <h3 className="text-base text-gray-900 font-semibold truncate">{name}</h3>
              {subtitle && <p className="text-xs text-gray-500 truncate mt-0.5">{subtitle}</p>}
            </div>
          </div>

          <div className="flex items-center gap-1.5 shrink-0 flex-wrap justify-end">
            {isDefault && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-100 text-blue-800 border border-blue-200">
                {t('common.default')}
              </span>
            )}
            {onLedger && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onLedger();
                }}
                className="inline-flex items-center justify-center p-1.5 rounded-lg text-indigo-600 bg-indigo-50 hover:bg-indigo-100 transition-colors"
                title={t('common.ledger')}
              >
                <BookOpen className="w-4 h-4" />
              </button>
            )}

            {onEdit && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                }}
                className="inline-flex items-center justify-center p-1.5 rounded-lg text-blue-600 bg-blue-50 hover:bg-blue-100 border border-blue-200 transition-colors"
                title={t('common.edit')}
              >
                <Pencil className="w-4 h-4" />
              </button>
            )}            {onDelete && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
                className="inline-flex items-center justify-center p-1.5 rounded-lg text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 transition-colors"
                title={t('common.delete')}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        <div className={statGridClass}>
          {stats.map((stat, idx) => {
            const StatIcon = stat.icon;
            return (
              <div key={idx} className={`${stat.bgClass} rounded-lg p-3 text-center min-h-[76px] flex flex-col justify-center`}>
                <StatIcon className={`w-4 h-4 ${stat.iconColorClass} mx-auto mb-1`} />
                <p className="text-sm sm:text-base font-semibold text-gray-900 leading-tight">
                  {formatMoney(stat.value || 0)}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">{stat.label}</p>
              </div>
            );
          })}
        </div>

        <div className="mt-auto min-h-[36px] pt-4">
          <div className="flex items-center justify-between text-xs mb-1.5 gap-2">
            <span className="text-gray-500 shrink-0">{progressLabel}</span>
            <span className="font-medium text-gray-900 text-right">
              {progressPercentage}% {progressSuffix}
            </span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
            <div
              className="h-full rounded-full transition-all bg-blue-500"
              style={{ width: `${barWidth}%` }}
            />
          </div>
        </div>

        {(phone || email || address) && (
          <div className="mt-4 pt-4 border-t border-gray-100 space-y-1.5">
            {phone && (
              <div className="flex items-start gap-2 text-xs text-gray-500">
                <Phone className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                <span className="break-all">{phone}</span>
              </div>
            )}
            {email && (
              <div className="flex items-start gap-2 text-xs text-gray-500">
                <Mail className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                <span className="break-all line-clamp-1">{email}</span>
              </div>
            )}
            {address && (
              <div className="flex items-start gap-2 text-xs text-gray-500">
                <MapPin className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                <span className="line-clamp-2 break-words">{address}</span>
              </div>
            )}
          </div>
        )}

        {actions.length > 0 && (
          <div className="flex flex-col sm:flex-row gap-2 mt-4 pt-4 border-t border-gray-100">
            {actions.map((action, idx) => {
              const ActionIcon = action.icon;
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    action.onClick();
                  }}
                  className={`flex-1 py-2 sm:py-1.5 text-xs font-medium ${action.textClass} ${action.bgClass} ${action.hoverClass} rounded-lg transition-colors flex items-center justify-center gap-1.5`}
                >
                  <ActionIcon className="w-3.5 h-3.5 shrink-0" />
                  <span className="truncate">{action.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
